import type { Layout } from '../../data/types';
import type { Rng } from '../../core/rng';
import type { Access } from './itinerary';
import { doorTravel, stay, travel } from './itinerary';
import { distance, type StreetGraph } from './streetGraph';
import { pedestrianPath } from './pedestrianPath';
import type { LifeClearance } from './routes';
import type { LifeRoute, LifeStep } from './types';
import type { Vec2 } from '../../geo/types';

function houseVisits(
  home: Access,
  visits: readonly { from: Access; to: Access; path: readonly Vec2[] }[],
  speed: number,
  clearance: LifeClearance,
  rng: Rng
): LifeStep[] {
  const steps: LifeStep[] = [];
  stay(steps, home, 32, 'inside');
  for (const { from, to, path } of visits) {
    doorTravel(steps, from, true, speed, 'parcel');
    travel(
      steps,
      pedestrianPath([from.approach, ...path, to.approach], clearance, 0.65),
      speed,
      'parcel'
    );
    stay(steps, to, 3, 'wait');
    doorTravel(steps, to, false, speed, 'parcel');
    if (to !== home) stay(steps, to, rng.range(45, 80), 'inside');
  }
  return steps;
}

/** Visits use ordinary occupied houses and their rendered doors, not hospital wards or shops. */
export function physicianRoutines(
  layout: Layout,
  graph: StreetGraph,
  homes: readonly Access[],
  used: Set<string>,
  clearance: LifeClearance,
  rng: Rng
): LifeRoute[] {
  const quayIds = new Set(
    layout.streets.filter((street) => street.kind === 'quay').map((street) => street.id)
  );
  const clients = homes.filter((home) => quayIds.has(home.entry.streetId));
  for (const home of homes) {
    if (used.has(home.entry.id)) continue;
    const candidates = clients
      .filter(
        (client) =>
          client !== home &&
          distance(client.approach, home.approach) > 40 &&
          distance(client.approach, home.approach) < 180
      )
      .sort((a, b) => distance(a.approach, home.approach) - distance(b.approach, home.approach));
    for (const first of candidates) {
      const out = graph.path(home.node, first.node);
      if (!out) continue;
      for (const second of candidates) {
        if (second === first || distance(first.approach, second.approach) < 35) continue;
        const between = graph.path(first.node, second.node),
          back = graph.path(second.node, home.node);
        if (!between || !back) continue;
        const speed = 0.96;
        const steps = houseVisits(
          home,
          [
            { from: home, to: first, path: out },
            { from: first, to: second, path: between },
            { from: second, to: home, path: back },
          ],
          speed,
          clearance,
          rng
        );
        used.add(home.entry.id);
        const duration = steps.reduce((sum, step) => sum + step.seconds, 0);
        return [
          {
            id: 'physician-house-visits',
            role: 'physician',
            streetId: home.entry.streetId,
            a: home.approach,
            b: first.approach,
            radius: 0.48,
            speed,
            scale: 1,
            phase: rng.range(35, duration - 10),
            daytime: true,
            routine: steps,
          },
        ];
      }
    }
  }
  return [];
}
